import React from 'react';

import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import {
  Asset,
  ButtonIcon,
  Icon,
  StarRating,
  TAsset,
  TertiaryButton,
  TextLarge,
  TextRegular,
} from '@hz-design-system/web-ui';

import type { UserReviews } from '../types';

import Classes from './ReviewsDialog.module.scss';

type ExternalProviderConfig = {
  logo: TAsset;
  source: string;
  sourceAsset?: TAsset;
};

const EXTERNAL_REVIEW_PROVIDER_CONFIG: Record<string, ExternalProviderConfig> = {
  GOOGLE_REVIEWS: {
    logo: 'logo-googlemaps',
    source: 'Google Maps',
    sourceAsset: 'social-badge-google',
  },
  KLANTENVERTELLEN: {
    logo: 'logo-klantenvertellen',
    source: 'Klantenvertellen.nl',
  },
};

type ExternalReviewsContentProps = {
  sellerName: string;
  reviewsData: UserReviews;
  onExternalReviewsUrlClick?: () => void;
};

const ExternalReviewsContent = ({
  sellerName,
  reviewsData,
  onExternalReviewsUrlClick,
}: ExternalReviewsContentProps) => {
  const { t } = useI18nContext();

  const {
    rating = 0,
    lastReviews: reviews = [],
    externalReviewsUrl,
    numberOfReviews,
    reviewSystem = '',
    sorting = '',
  } = reviewsData;

  const config: ExternalProviderConfig | undefined = EXTERNAL_REVIEW_PROVIDER_CONFIG[reviewSystem];

  return (
    <div className={Classes.externalReviewsContentRoot}>
      <div className={Classes.heading}>
        <div className={Classes.headingBody}>
          <TextLarge as="div" className={`${Classes.externalReviewsHeadingName} TextEllipsis`}>
            {sellerName}
          </TextLarge>
          <div className={Classes.summary}>
            <Icon name="saved" color="text-brand-primary" />
            <TextLarge as="strong">{rating.toFixed(1)}</TextLarge>
            <TextLarge className={Classes.ratingText}>
              {t('reviewsDialogUi:external.reviewCount', { count: numberOfReviews })}
            </TextLarge>
          </div>
          <div className={Classes.externalReviewHeadingMeta}>
            {t('reviewsDialogUi:external.providedBy', { provider: config?.source })}
          </div>
        </div>
        {config?.logo && (
          <div className={Classes.providerLogo}>
            <Asset.Square size="xxl" name={config.logo} className={Classes.logo} />
          </div>
        )}
      </div>
      <TextRegular utilities={['marginTopM']}>
        <Trans i18nKey={`reviewsDialogUi:external.sorting.${sorting.toLowerCase()}`} />
      </TextRegular>
      {reviews.map((review, index) => (
        <div key={`${index}-${review.name}`} className={Classes.review}>
          <div className={Classes.reviewerName}>{review.name}</div>
          <div className={Classes.reviewMetaInfo}>
            {Boolean(review.place) && <span className={Classes.reviewerPlace}>{review.place}</span>}
            {Boolean(review.date) && <span>{review.date}</span>}
            {Boolean(config?.sourceAsset) && (
              <>
                <Trans i18nKey="reviewsDialogUi:external.reviewOn" tagName="span" />
                <Asset.Square name={config!.sourceAsset!} size="s" />
                <span>{config?.source}</span>
              </>
            )}
          </div>
          <StarRating label="" rating={review.rating} />
          <p className={Classes.reviewText}>"{review.text}"</p>
        </div>
      ))}
      {externalReviewsUrl && (
        <div className={Classes.footerButton}>
          <TertiaryButton
            as="a"
            href={externalReviewsUrl}
            target="_blank"
            rel="nofollow"
            onClick={onExternalReviewsUrlClick}
          >
            <ButtonIcon name="external-link" />
            {t('reviewsDialogUi:external.readMore')}
          </TertiaryButton>
        </div>
      )}
    </div>
  );
};

export default ExternalReviewsContent;
